import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { getAllCategories } from '../../../containers/CategoriesContainer';
import { FaChevronRight } from 'react-icons/fa';
import { useLanguage } from '../../../LanguageContext';

let _cachedCategories = null;
function getCategoriesOnce() {
  if (_cachedCategories) return _cachedCategories;
  _cachedCategories = getAllCategories();
  return _cachedCategories;
}
export function invalidateCategoriesCache() {
  _cachedCategories = null;
}

function useClickOutside(ref, isActive, onClose) {
  useEffect(() => {
    if (!isActive) return;
    const timer = setTimeout(() => {
      const handler = (e) => {
        if (ref.current && !ref.current.contains(e.target)) {
          onClose();
        }
      };
      document.addEventListener('click', handler);
      return () => document.removeEventListener('click', handler);
    }, 0);
    return () => clearTimeout(timer);
  }, [isActive, ref, onClose]);
}

// ─── CategoryItem: cada categoria raiz com submenu próprio ───────────────────
const CategoryItem = React.memo(function CategoryItem({ category, subs, handleRemovePage }) {
  const [isOpen, setIsOpen] = useState(false);
  const itemRef = useRef(null);
  const close = useCallback(() => setIsOpen(false), []);

  useClickOutside(itemRef, isOpen, close);
  useEffect(() => () => setIsOpen(false), []);

  return (
    <li ref={itemRef} className="nav_sub-menu_item row">
      <button
        className="nav_sub-menu_link nav-dropdown col-10 m-0"
        style={{ background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
        onClick={() => setIsOpen((v) => !v)}
      >
        <span>{category.Descricao}</span>
      </button>
      <ul className={`list-unstyled mb-0 nav_sub-menu nav_sub-menu--interno ${isOpen ? 'active' : ''}`}>
        {subs.map((sub, subIndex) => {
          const url = `/busca?c=${sub.idClassificacaoNivelAnterior}&s=${sub.idClassificacaoNivel}`;
          return (
            <li key={`subCat-${sub.idClassificacaoNivel}-${subIndex}`} className="nav_sub-menu_item">
              <a href={url} className="nav_sub-menu_link" onClick={handleRemovePage}>
                {sub.Descricao}
              </a>
            </li>
          );
        })}
      </ul>
      <FaChevronRight className="fa mt-2" size={14} />
    </li>
  );
});

// ─── MenuItem ────────────────────────────────────────────────────────────────
const MenuItem = React.memo(function MenuItem({ descricao, children }) {
  const [active, setActive] = useState(false);
  const menuRef = useRef(null);
  const close = useCallback(() => setActive(false), []);

  useClickOutside(menuRef, active, close);
  useEffect(() => () => setActive(false), []);

  return (
    <li className="nav-item col-6 m-0 p-0" ref={menuRef}>
      <button
        style={{
          width: '100%',
          border: 'none',
          backgroundColor: active ? '#005f8d' : '#008ed1',
          color: 'white',
          cursor: 'pointer',
        }}
        className="nav-link nav-dropdown px-0 pr-4 justify-content-center"
        onClick={() => setActive((v) => !v)}
      >
        {descricao}
      </button>
      <ul className={`list-unstyled col-12 mb-0 nav_sub-menu nav_sub-menu--interno ${active ? 'active' : ''}`}>
        {children}
      </ul>
    </li>
  );
});

// ─── SubMenuItem ─────────────────────────────────────────────────────────────
const SubMenuItem = React.memo(function SubMenuItem({ descricao, children }) {
  const [isActive, setIsActive] = useState(false);
  const menuRef = useRef(null);
  const close = useCallback(() => setIsActive(false), []);

  useClickOutside(menuRef, isActive, close);
  useEffect(() => () => setIsActive(false), []);

  return (
    <li className="nav-item col-2 m-0 p-0" ref={menuRef}>
      <button
        className="nav-link nav-dropdown px-2 py-0 justify-content-center"
        style={{ background: 'none', border: 'none', cursor: 'pointer', width: '100%' }}
        onClick={() => setIsActive((v) => !v)}
      >
        {descricao}
      </button>
      <ul
        className={`list-unstyled mb-0 nav_sub-menu nav_sub-menu--interno position-absolute overflow-visible ${isActive ? 'active' : ''}`}
        style={{ zIndex: 1000 }}
      >
        {children}
      </ul>
    </li>
  );
});

// ─── Category ────────────────────────────────────────────────────────────────
const Category = ({ props }) => {
  const { translations } = useLanguage();

  const allCategories = useMemo(() => getCategoriesOnce(), []);

  const handleRemovePage = useCallback(() => {
    localStorage.removeItem('MyPageNumber');
  }, []);

  const rootCategories = useMemo(
    () => allCategories.filter((c) => c.idClassificacaoNivelAnterior === null),
    [allCategories]
  );

  const subCategoryMap = useMemo(() => {
    const map = new Map();
    allCategories.forEach((c) => {
      const pid = c.idClassificacaoNivelAnterior;
      if (pid !== null) {
        if (!map.has(pid)) map.set(pid, []);
        map.get(pid).push(c);
      }
    });
    map.forEach((arr) => arr.sort((a, b) => a.Descricao.localeCompare(b.Descricao)));
    return map;
  }, [allCategories]);

  const menuItems = useMemo(() => {
    if (props.id == null) {
      return rootCategories.slice(0, 10).map((category, index) => {
        const subs = (subCategoryMap.get(category.idClassificacaoNivel) || []).slice(0, 27);
        return (
          <CategoryItem
            key={`catNav-${category.idClassificacaoNivel}-${index}`}
            category={category}
            subs={subs}
            handleRemovePage={handleRemovePage}
          />
        );
      });
    } else {
      return (subCategoryMap.get(props.id) || []).map((category) => {
        const url = `/busca?c=${category.idClassificacaoNivelAnterior}&s=${category.idClassificacaoNivel}`;
        return (
          <li
            id={`allCats${category.idClassificacaoNivel}-${category.idClassificacaoNivelAnterior}`}
            key={`allCats${category.idClassificacaoNivel}-${category.idClassificacaoNivelAnterior}`}
            className="nav_sub-menu_item"
          >
            <a href={url} className="nav_sub-menu_link" onClick={handleRemovePage}>
              {category.Descricao}
            </a>
          </li>
        );
      });
    }
  }, [props.id, rootCategories, subCategoryMap, handleRemovePage]);


  const AllCategoriesItem = React.memo(function AllCategoriesItem({ rootCategories, label, handleRemovePage }) {
  const [isOpen, setIsOpen] = useState(false);
  const itemRef = useRef(null);
  const close = useCallback(() => setIsOpen(false), []);

  useClickOutside(itemRef, isOpen, close);
  useEffect(() => () => setIsOpen(false), []);

  return (
    <li ref={itemRef} className="nav_sub-menu_item todas-categorias row">
      <button
        className="nav_sub-menu_link nav-dropdown col-10 m-0"
        style={{ background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
        onClick={() => setIsOpen((v) => !v)}
      >
        <span>{label}</span>
      </button>
      <ul className={`list-unstyled mb-0 nav_sub-menu nav_sub-menu--interno ${isOpen ? 'active' : ''}`}>
        {rootCategories.map((category, index) => {
          const url = `/busca?c=${category.idClassificacaoNivel}`;
          return (
            <li key={`todas-categorias-${category.idClassificacaoNivel}-${index}`} className="nav_sub-menu_item">
              <a href={url} className="nav_sub-menu_link" onClick={handleRemovePage}>
                {category.Descricao}
              </a>
            </li>
          );
        })}
      </ul>
      <FaChevronRight className="fa mt-2" size={14} />
    </li>
  );
});

 const allCategoriesItem = useMemo(() => {
  if (props.id != null) return null;
  return (
    <AllCategoriesItem
      rootCategories={rootCategories}
      label={translations.MenuPrincipal?.Todas_las_categorias}
      handleRemovePage={handleRemovePage}
    />
  );
}, [props.id, rootCategories, translations, handleRemovePage]);

  return (
    <>
      {props.id == null ? (
        <MenuItem descricao={props.descricao}>
          {menuItems}
          {allCategoriesItem}
        </MenuItem>
      ) : (
        <SubMenuItem descricao={props.descricao}>
          {menuItems}
          {allCategoriesItem}
        </SubMenuItem>
      )}
    </>
  );
};

export default Category;